import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import CardChartDropDown from '@/components/common/CardChartDropDown';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import numberHumanizer from '@/helpers/numberHumanizer';
import timeStampToDate from '@/helpers/timeStampToDate';
import { useRef, useState, type ReactNode } from 'react';
import { Badge, Card, Col, Form, Row } from 'react-bootstrap';

export const borderClasses = [
  'border-200 border-bottom border-end pb-4',
  'border-200 border-md-200 border-bottom border-md-end pb-4 ps-3',
  'border-200 border-bottom border-end border-md-end-0 pb-4 pt-4 pt-md-0 ps-md-3',
  'border-200 border-md-200 border-bottom border-md-bottom-0 border-md-end pt-4 pb-md-0 ps-3 ps-md-0',
  'border-200 border-md-bottom-0 border-end pt-4 pb-md-0 ps-md-3',
  'pb-0 pt-4 ps-3'
];

export type CompetitorsLinearChartData = {
  datas: {
    data: (string | number)[];
    series: Array<{ name: string; data: (string | number)[] }>;
  };
};

type CompetitorsDateProps = {
  title: ReactNode;
  data: CompetitorsLinearChartData;
  tooltip: string;
};

const CompetitorsDate = ({ title, data, tooltip }: CompetitorsDateProps) => {
  const [selectedDate, setSelectedDate] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);
  return (
    <Card ref={cardRef}>
      <FalconCardHeader
        title={
          <div>
            {title} <HelpTooltip html={tooltip} />
          </div>
        }
        titleTag="h6"
        className="py-2"
        light
        endEl={
          <div className="d-flex gap-1">
            <Form.Select
              style={{
                minHeight: '100px !important',
                maxHeight: '100px !important',
                overflowY: 'visible !important'
              }}
              onChange={e => setSelectedDate(Number(e.target.value))}
              defaultValue={0}
              size="sm"
            >
              {data?.datas?.data.map((date, index) => (
                <option value={index} key={String(date)}>
                  {timeStampToDate(Number(date)).slice(8).toPersianDigit()}
                </option>
              ))}
            </Form.Select>
            <CardChartDropDown cardRef={cardRef} />
          </div>
        }
      />
      <Card.Body className="py-3 position-relative">
        <Row className="g-0">
          <div className="d-flex flex-wrap">
            {data?.datas.series.map((item, i) => (
              <Col
                key={item.name}
                xs={6}
                md={4}
                className={borderClasses[i] ?? borderClasses[0]}
              >
                <Label
                  title={item.name}
                  first={item.data[selectedDate]}
                  last={item.data[item.data.length - 1]}
                />
              </Col>
            ))}
          </div>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default CompetitorsDate;

type LabelProps = {
  title: string;
  first: string | number | undefined;
  last: string | number | undefined;
};

const Label = ({ title, first, last }: LabelProps) => {
  const firstNum = first != null ? Number(first) : 0;
  const lastNum = last != null ? Number(last) : 0;
  return (
    <>
      <h6 className="pb-1 text-700">{title}</h6>
      <p className="font-sans-serif lh-1 mb-1 fs-2 fs-md-2 fw-bold">
        {first ? numberHumanizer(Number(first)) : 0}
      </p>
      <div className="d-flex flex-wrap align-items-center">
        <h6 className="fs-0 text-500 fw mb-0">
          {last ? numberHumanizer(Number(last)) : 0}
        </h6>
        <h6 className="fs-0 d-flex align-items-center my-1 text-700 mb-1">
          {last && first && (
            <Badge
              pill
              bg="transparent"
              className={classNames(
                lastNum - firstNum === 0
                  ? ''
                  : lastNum - firstNum > 0
                    ? 'text-success'
                    : 'text-danger',
                'px-0'
              )}
            >
              {lastNum - firstNum !== 0 ? (
                <>
                  <FontAwesomeIcon
                    icon={lastNum - firstNum > 0 ? 'caret-up' : 'caret-down'}
                    className="fs--2 ms-2 me-1"
                  />
                  {numberHumanizer(
                    Number(
                      lastNum - firstNum > 0
                        ? lastNum - firstNum
                        : (lastNum - firstNum) * -1
                    )
                  )}
                </>
              ) : (
                <span className="fs--2 ms-2 me-1">
                  {numberHumanizer(Number(lastNum - firstNum))}
                </span>
              )}
            </Badge>
          )}
        </h6>
      </div>
    </>
  );
};
